import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getUserDetails } from '../../redux/userRelated/userHandle';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Collapse, Table, TableBody, TableHead, Typography } from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { calculateOverallAttendancePercentage, calculateSubjectAttendancePercentage, groupAttendanceBySubject } from '../../components/attendanceCalculator';
import CustomPieChart from '../../components/CustomPieChart';
import { StyledTableCell, StyledTableRow } from '../../components/styles';
import LoadingSpinner from '../../components/LoadingSpinner';

const TeacherViewStudent = () => {
    const navigate = useNavigate();
    const params = useParams();
    const dispatch = useDispatch();
    const { currentUser, userDetails, loading, error } = useSelector((state) => state.user);

    const address = "Student";
    const studentID = params.id;
    const teachSubject = currentUser.teachSubject?.subName;
    const teachSubjectID = currentUser.teachSubject?._id;

    useEffect(() => {
        dispatch(getUserDetails(studentID, address));
    }, [dispatch, studentID]);

    const [openStates, setOpenStates] = useState({});

    const handleOpen = (subId) => {
        setOpenStates(prev => ({ ...prev, [subId]: !prev[subId] }));
    };

    // Process attendance data
    const subjectAttendance = userDetails?.attendance
        ?.filter(att => att.subName?.subName === teachSubject)
        ?.map(att => ({
            ...att,
            status: att.status.charAt(0).toUpperCase() + att.status.slice(1).toLowerCase()
        })) || [];

    const groupedAttendance = groupAttendanceBySubject(subjectAttendance);
    const overallAttendancePercentage = calculateOverallAttendancePercentage(subjectAttendance);
    const overallAbsentPercentage = 100 - overallAttendancePercentage;

    const chartData = [
        { name: 'Present', value: overallAttendancePercentage },
        { name: 'Absent', value: overallAbsentPercentage }
    ].filter(item => item.value > 0);

    if (loading) return <LoadingSpinner />;
    if (error) return <Typography color="error">Error: {error}</Typography>;

    return (
        <Box sx={{ padding: { xs: 1, sm: 3 }, backgroundColor: '#f0f8ff', minHeight: '100vh' }}>
            <Typography variant="h4" gutterBottom sx={{ ...styles.header, fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                Student Details
            </Typography>
            
            <Box sx={styles.detailsContainer}>
                <Typography variant="h6" sx={{ ...styles.detailText, fontSize: { xs: '1rem', sm: '1.25rem' } }}>Name: {userDetails.name}</Typography>
                <Typography variant="h6" sx={{ ...styles.detailText, fontSize: { xs: '1rem', sm: '1.25rem' } }}>Roll Number: {userDetails.rollNum}</Typography>
                <Typography variant="h6" sx={{ ...styles.detailText, fontSize: { xs: '1rem', sm: '1.25rem' } }}>Class: {userDetails.sclassName?.sclassName}</Typography>
                <Typography variant="h6" sx={{ ...styles.detailText, fontSize: { xs: '1rem', sm: '1.25rem' } }}>School: {userDetails.school?.schoolName}</Typography>
            </Box>

            <Typography variant="h5" gutterBottom sx={{ ...styles.header, fontSize: { xs: '1.25rem', sm: '1.75rem' } }}>
                Attendance
            </Typography>
            
            {subjectAttendance.length > 0 ? (
                <>
                    {Object.entries(groupedAttendance).map(([subName, { present, allData, subId, sessions }], index) => (
                        <Box key={index} sx={{ ...styles.tableContainer, overflowX: 'auto' }}>
                            <Table>
                                <TableHead>
                                    <StyledTableRow>
                                        <StyledTableCell sx={styles.tableHeader}>Subject</StyledTableCell>
                                        <StyledTableCell sx={styles.tableHeader}>Present</StyledTableCell>
                                        <StyledTableCell sx={styles.tableHeader}>Total Sessions</StyledTableCell>
                                        <StyledTableCell sx={styles.tableHeader}>Attendance Percentage</StyledTableCell>
                                        <StyledTableCell align="center" sx={styles.tableHeader}>Details</StyledTableCell>
                                    </StyledTableRow>
                                </TableHead>
                                <TableBody>
                                    <StyledTableRow>
                                        <StyledTableCell sx={styles.tableCell}>{subName}</StyledTableCell>
                                        <StyledTableCell sx={styles.tableCell}>{present}</StyledTableCell>
                                        <StyledTableCell sx={styles.tableCell}>{sessions}</StyledTableCell>
                                        <StyledTableCell sx={styles.tableCell}>
                                            {sessions > 0 ? 
                                                `${calculateSubjectAttendancePercentage(present, sessions).toFixed(2)}%` : 
                                                'N/A'}
                                        </StyledTableCell>
                                        <StyledTableCell align="center" sx={styles.tableCell}>
                                            <Button 
                                                variant="contained" 
                                                onClick={() => handleOpen(subId)}
                                                endIcon={openStates[subId] ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                                sx={{ ...styles.button, fontSize: { xs: '0.75rem', sm: '0.875rem' }, px: { xs: 1, sm: 3 } }}
                                            >
                                                {openStates[subId] ? 'Hide' : 'Show'}
                                            </Button>
                                        </StyledTableCell>
                                    </StyledTableRow>
                                    <StyledTableRow>
                                        <StyledTableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
                                            <Collapse in={openStates[subId]} timeout="auto" unmountOnExit>
                                                <Box sx={{ margin: 1 }}>
                                                    <Typography variant="h6" gutterBottom sx={{ ...styles.subHeader, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                                        Attendance Details
                                                    </Typography>
                                                    <Table size="small">
                                                        <TableHead>
                                                            <StyledTableRow>
                                                                <StyledTableCell sx={styles.tableHeader}>Date</StyledTableCell>
                                                                <StyledTableCell align="right" sx={styles.tableHeader}>Status</StyledTableCell>
                                                            </StyledTableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {allData.map((data, index) => {
                                                                const date = new Date(data.date);
                                                                const dateString = date.toString() !== "Invalid Date" 
                                                                    ? date.toLocaleDateString() 
                                                                    : "Invalid Date";
                                                                return (
                                                                    <StyledTableRow key={index}>
                                                                        <StyledTableCell sx={styles.tableCell}>{dateString}</StyledTableCell>
                                                                        <StyledTableCell 
                                                                            align="right"
                                                                            sx={{ 
                                                                                color: data.status === 'Present' 
                                                                                    ? '#008080' // Teal for Present
                                                                                    : '#ff6347', // Tomato for Absent
                                                                                fontWeight: 'bold'
                                                                            }}
                                                                        >
                                                                            {data.status}
                                                                        </StyledTableCell>
                                                                    </StyledTableRow>
                                                                );
                                                            })}
                                                        </TableBody>
                                                    </Table>
                                                </Box>
                                            </Collapse>
                                        </StyledTableCell>
                                    </StyledTableRow>
                                </TableBody>
                            </Table>
                        </Box>
                    ))}

                    {chartData.length > 0 && (
                        <Box sx={{ mt: 4, textAlign: 'center' }}>
                            <Typography variant="h6" gutterBottom sx={{ ...styles.subHeader, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                Overall Attendance: {overallAttendancePercentage.toFixed(2)}%
                            </Typography>
                            <CustomPieChart data={chartData} />
                        </Box>
                    )}

                    <Box sx={{ mt: 3, textAlign: 'center' }}>
                        <Button
                            variant="contained"
                            onClick={() => navigate(`/Teacher/class/student/attendance/${studentID}/${teachSubjectID}`)}
                            sx={{ ...styles.button, fontSize: { xs: '0.75rem', sm: '0.875rem' }, px: { xs: 1, sm: 3 } }}
                        >
                            Add Attendance
                        </Button>
                    </Box>
                </>
            ) : (
                <Typography variant="body1" sx={{ ...styles.noDataText, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    No attendance records found for {teachSubject}
                </Typography>
            )}

            <Typography variant="h5" gutterBottom sx={{ ...styles.header, fontSize: { xs: '1.25rem', sm: '1.75rem' } }}>
                Subject Marks
            </Typography>

            {userDetails.examResult?.filter(result => result.subName?.subName === teachSubject).map((result, index) => (
                <Box key={index} sx={{ ...styles.tableContainer, overflowX: 'auto' }}>
                    <Table>
                        <TableHead>
                            <StyledTableRow>
                                <StyledTableCell sx={styles.tableHeader}>Subject</StyledTableCell>
                                <StyledTableCell sx={styles.tableHeader}>Marks Obtained</StyledTableCell>
                            </StyledTableRow>
                        </TableHead>
                        <TableBody>
                            <StyledTableRow>
                                <StyledTableCell sx={styles.tableCell}>{result.subName.subName}</StyledTableCell>
                                <StyledTableCell sx={styles.tableCell}>{result.marksObtained}</StyledTableCell>
                            </StyledTableRow>
                        </TableBody>
                    </Table>
                </Box>
            ))}

            {!userDetails.examResult?.some(result => result.subName?.subName === teachSubject) && (
                <Typography variant="body1" sx={{ ...styles.noDataText, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    No marks recorded for {teachSubject}
                </Typography>
            )}

            <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Button
                    variant="contained"
                    onClick={() => navigate(`/Teacher/class/student/marks/${studentID}/${teachSubjectID}`)}
                    sx={{ ...styles.button, fontSize: { xs: '0.75rem', sm: '0.875rem' }, px: { xs: 1, sm: 3 } }}
                >
                    Add Marks
                </Button>
            </Box>
        </Box>
    );
};

const styles = {
    header: {
        color: '#008080', // Teal
        fontWeight: 'bold',
        mb: 2,
    },
    subHeader: {
        color: '#ffd700', // Gold
        fontWeight: 'bold',
        mb: 2,
    },
    detailsContainer: {
        backgroundColor: '#ffffff',
        borderRadius: 2,
        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
        p: { xs: 2, sm: 3 },
        mb: 4,
    },
    detailText: {
        color: '#333',
        mb: 1,
    },
    tableContainer: {
        backgroundColor: '#ffffff',
        borderRadius: 2,
        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
        p: { xs: 1, sm: 2 },
        mb: 3,
    },
    tableHeader: {
        backgroundColor: '#008080', // Teal
        color: '#ffffff',
        fontWeight: 'bold',
    },
    tableCell: {
        color: '#333',
    },
    button: {
        backgroundColor: '#ffd700', // Gold
        color: '#000',
        '&:hover': {
            backgroundColor: '#008080', // Teal on hover
            color: '#fff',
        },
        fontWeight: 'bold',
        py: 1,
    },
    noDataText: {
        color: '#ff6347', // Tomato
        mb: 2,
    },
};

export default TeacherViewStudent;